import React, { useState, useEffect } from 'react';
import api from '../api/client';
import { HiOutlinePlus, HiOutlineXMark } from 'react-icons/hi2';
import toast from 'react-hot-toast';

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [pagination, setPagination] = useState({});
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ customer_name: '', total_amount: '', invoice_date: '', currency: 'PKR' });

  useEffect(() => { load(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, page]);

  const load = async () => {
    try {
      const res = await api.get('/invoices', { params: { status: status || undefined, page, limit: 20 } });
      setInvoices(res.data.data.invoices);
      setPagination(res.data.data.pagination);
    } catch { } finally { setLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/invoices', { ...form, total_amount: parseFloat(form.total_amount) });
      toast.success('Invoice created');
      setShowModal(false);
      setForm({ customer_name: '', total_amount: '', invoice_date: '', currency: 'PKR' });
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const handleAction = async (id, action) => {
    try { await api.patch(`/invoices/${id}/${action}`); toast.success(`Invoice ${action}d`); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const statusBadge = (s) => {
    const colors = { draft: 'bg-gray-100 text-gray-600', final: 'bg-blue-100 text-blue-700', sent: 'bg-green-100 text-green-700', cancelled: 'bg-red-100 text-red-700' };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[s] || ''}`}>{s}</span>;
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <h1 className="text-xl font-bold text-dark-800">Invoices</h1>
        <button onClick={() => setShowModal(true)} className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors">
          <HiOutlinePlus className="w-4 h-4" /> New Invoice
        </button>
      </div>

      <div className="mb-4">
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          className="px-3 py-2 bg-white border border-dark-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500">
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="final">Final</option>
          <option value="sent">Sent</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      <div className="bg-white rounded-xl border border-dark-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-dark-200">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-dark-500">Invoice #</th>
                <th className="text-left px-4 py-3 font-medium text-dark-500">Customer</th>
                <th className="text-left px-4 py-3 font-medium text-dark-500 hidden sm:table-cell">Date</th>
                <th className="text-right px-4 py-3 font-medium text-dark-500">Amount</th>
                <th className="text-left px-4 py-3 font-medium text-dark-500">Status</th>
                <th className="text-right px-4 py-3 font-medium text-dark-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-100">
              {loading ? (
                <tr><td colSpan={6} className="text-center py-8 text-dark-400">Loading...</td></tr>
              ) : invoices.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-8 text-dark-400">No invoices found</td></tr>
              ) : invoices.map((inv) => (
                <tr key={inv.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium">{inv.invoice_no}</td>
                  <td className="px-4 py-3">{inv.customer_name}</td>
                  <td className="px-4 py-3 text-dark-500 hidden sm:table-cell">{inv.invoice_date}</td>
                  <td className="px-4 py-3 text-right font-medium">{inv.currency} {Number(inv.total_amount).toLocaleString()}</td>
                  <td className="px-4 py-3">{statusBadge(inv.status)}</td>
                  <td className="px-4 py-3 text-right space-x-1">
                    {inv.status === 'draft' && <button onClick={() => handleAction(inv.id, 'finalize')} className="text-xs text-blue-500 hover:text-blue-700">Finalize</button>}
                    {inv.status === 'final' && <button onClick={() => handleAction(inv.id, 'send')} className="text-xs text-green-500 hover:text-green-700">Send</button>}
                    {inv.status !== 'cancelled' && <button onClick={() => handleAction(inv.id, 'cancel')} className="text-xs text-red-400 hover:text-red-600">Cancel</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pagination.totalPages > 1 && (
          <div className="flex justify-between items-center px-4 py-3 border-t border-dark-200 text-sm text-dark-400">
            <span>Page {pagination.page} of {pagination.totalPages}</span>
            <div className="flex gap-2">
              <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-3 py-1 border border-dark-200 rounded hover:bg-gray-50 disabled:opacity-50">Prev</button>
              <button disabled={page >= pagination.totalPages} onClick={() => setPage(page + 1)} className="px-3 py-1 border border-dark-200 rounded hover:bg-gray-50 disabled:opacity-50">Next</button>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md shadow-xl">
            <div className="flex justify-between items-center p-4 border-b border-dark-200">
              <h2 className="font-bold">New Invoice</h2>
              <button onClick={() => setShowModal(false)}><HiOutlineXMark className="w-5 h-5 text-dark-400" /></button>
            </div>
            <form onSubmit={handleCreate} className="p-4 space-y-3">
              <input type="text" required placeholder="Customer name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                className="w-full px-3 py-2 border border-dark-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500" />
              <input type="number" required placeholder="Amount" step="0.01" value={form.total_amount} onChange={(e) => setForm({ ...form, total_amount: e.target.value })}
                className="w-full px-3 py-2 border border-dark-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500" />
              <input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })}
                className="w-full px-3 py-2 border border-dark-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500" />
              <button type="submit" className="w-full bg-primary-500 text-white py-2 rounded-lg font-medium text-sm hover:bg-primary-600">Create Invoice</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
